import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import type { PrimeVueConfiguration, PrimeVueLocaleOptions } from 'primevue/config'
import { reactive } from 'vue'

import { buttonConfig, buttonPt } from './button'
import { accordionConfig, cardConfig, panelConfig } from './card'
import { badgeConfig, chipConfig, tagConfig } from './chip'
import { datePickerConfig } from './datepicker'
import { dialogConfig, drawerConfig, popoverConfig } from './drawer'
import {
  dividerConfig,
  messageConfig,
  progressBarConfig,
  skeletonConfig,
  toastConfig,
} from './feedback'
import { floatLabelConfig } from './floatlabel'
import { iconFieldConfig, inputNumberConfig, inputTextConfig, textareaConfig } from './input'
import { selectButtonConfig, selectConfig } from './select'
import { tabsConfig } from './tabs'
import { checkboxConfig, radioButtonConfig, ratingConfig, toggleSwitchConfig } from './toggle'
import { PRIME_LOCALES } from './locale'
import { getLanguage } from '@/utils/language'

/**
 * Брендированный preset Oqtepa Lavash.
 * Все значения ссылаются на CSS-переменные из assets/styles/tokens.css —
 * поэтому тема живёт в одном месте и переключение light/dark «протекает»
 * в компоненты PrimeVue без дублирования colorScheme.
 */
const oqtepaPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: 'var(--radius-xs)',
      sm: 'var(--radius-s)',
      md: 'var(--radius-m)',
      lg: 'var(--radius-l)',
      xl: 'var(--radius-xl)',
    },
  },

  semantic: {
    primary: {
      50: 'var(--accent-50)',
      100: 'var(--accent-100)',
      200: 'var(--accent-200)',
      300: 'var(--accent-300)',
      400: 'var(--accent-400)',
      500: 'var(--accent-500)',
      600: 'var(--accent-600)',
      700: 'var(--accent-700)',
      800: 'var(--accent-800)',
      900: 'var(--accent-900)',
      950: 'var(--accent-900)',
    },

    transitionDuration: '.15s',
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: 'none',
    },

    /** Геометрия всех полей ввода: высота 50px при 13px шрифте. */
    formField: {
      paddingX: 'var(--space-7)',
      paddingY: 'var(--space-7)',
      borderRadius: 'var(--radius-xl)',
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: 'none',
      },
      sm: { fontSize: '13px', paddingX: 'var(--space-6)', paddingY: 'var(--space-5)' },
      lg: { fontSize: '16px', paddingX: 'var(--space-8)', paddingY: 'var(--space-8)' },
    },

    content: {
      borderRadius: 'var(--radius-xl)',
    },

    overlay: {
      select: { borderRadius: 'var(--radius-xl)', shadow: 'var(--shadow-card)' },
      popover: { borderRadius: 'var(--radius-l)', shadow: 'var(--shadow-card)' },
      modal: { borderRadius: 'var(--radius-xl)', shadow: 'var(--shadow-card)', padding: '0' },
    },

    colorScheme: {
      light: {
        surface: {
          0: 'var(--white)',
          50: 'var(--secondary-50)',
          100: 'var(--secondary-100)',
          200: 'var(--secondary-200)',
          300: 'var(--secondary-300)',
          400: 'var(--secondary-400)',
          500: 'var(--secondary-500)',
          600: 'var(--secondary-600)',
          700: 'var(--secondary-700)',
          800: 'var(--secondary-800)',
          900: 'var(--secondary-900)',
          950: 'var(--secondary-950)',
        },
        primary: {
          color: 'var(--accent-500)',
          contrastColor: 'var(--accent-surface-color)',
          hoverColor: 'var(--accent-400)',
          activeColor: 'var(--accent-600)',
        },
        content: {
          background: 'var(--surface-card)',
          hoverBackground: 'var(--surface-muted)',
          borderColor: 'var(--border-color)',
          color: 'var(--text-color)',
          hoverColor: 'var(--text-color)',
        },
        text: {
          color: 'var(--text-color)',
          hoverColor: 'var(--text-color)',
          mutedColor: 'var(--text-color-secondary)',
          hoverMutedColor: 'var(--text-color-muted)',
        },
        formField: {
          background: 'var(--surface-muted)',
          disabledBackground: 'var(--secondary-100)',
          filledBackground: 'var(--surface-muted)',
          filledHoverBackground: 'var(--surface-muted)',
          filledFocusBackground: 'var(--surface-muted)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          focusBorderColor: 'var(--secondary-200)',
          invalidBorderColor: 'var(--primary-500)',
          color: 'var(--text-color)',
          disabledColor: 'var(--text-color-secondary)',
          placeholderColor: 'var(--text-color-secondary)',
          floatLabelColor: 'var(--text-color-secondary)',
          iconColor: 'var(--icon-color)',
          shadow: 'none',
        },
        mask: {
          background: 'rgb(15 15 5 / 40%)',
          color: 'var(--text-color-inverse)',
        },
      },

      dark: {
        surface: {
          0: 'var(--secondary-950)',
          50: 'var(--secondary-900)',
          100: 'var(--secondary-800)',
          200: 'var(--secondary-700)',
          300: 'var(--secondary-600)',
          400: 'var(--secondary-500)',
          500: 'var(--secondary-400)',
          600: 'var(--secondary-300)',
          700: 'var(--secondary-200)',
          800: 'var(--secondary-100)',
          900: 'var(--secondary-50)',
          950: 'var(--white)',
        },
        primary: {
          color: 'var(--accent-500)',
          contrastColor: 'var(--accent-surface-color)',
          hoverColor: 'var(--accent-400)',
          activeColor: 'var(--accent-600)',
        },
        content: {
          background: 'var(--surface-card)',
          hoverBackground: 'var(--surface-muted)',
          borderColor: 'var(--border-color)',
          color: 'var(--text-color)',
          hoverColor: 'var(--text-color)',
        },
        text: {
          color: 'var(--text-color)',
          hoverColor: 'var(--text-color)',
          mutedColor: 'var(--text-color-secondary)',
          hoverMutedColor: 'var(--text-color-muted)',
        },
        formField: {
          background: 'var(--surface-muted)',
          disabledBackground: 'var(--secondary-800)',
          filledBackground: 'var(--surface-muted)',
          filledHoverBackground: 'var(--surface-muted)',
          filledFocusBackground: 'var(--surface-muted)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          focusBorderColor: 'var(--secondary-700)',
          invalidBorderColor: 'var(--primary-500)',
          color: 'var(--text-color)',
          disabledColor: 'var(--text-color-secondary)',
          placeholderColor: 'var(--text-color-secondary)',
          floatLabelColor: 'var(--text-color-secondary)',
          iconColor: 'var(--text-color-secondary)',
          shadow: 'none',
        },
        mask: {
          background: 'rgb(0 0 0 / 60%)',
          color: 'var(--text-color)',
        },
      },
    },
  },

  components: {
    accordion: accordionConfig(),
    badge: badgeConfig(),
    button: buttonConfig(),
    card: cardConfig(),
    checkbox: checkboxConfig(),
    chip: chipConfig(),
    datepicker: datePickerConfig(),
    dialog: dialogConfig(),
    divider: dividerConfig(),
    drawer: drawerConfig(),
    floatlabel: floatLabelConfig(),
    iconfield: iconFieldConfig(),
    inputnumber: inputNumberConfig(),
    inputtext: inputTextConfig(),
    message: messageConfig(),
    panel: panelConfig(),
    popover: popoverConfig(),
    progressbar: progressBarConfig(),
    radiobutton: radioButtonConfig(),
    rating: ratingConfig(),
    select: selectConfig(),
    selectbutton: selectButtonConfig(),
    skeleton: skeletonConfig(),
    tabs: tabsConfig(),
    tag: tagConfig(),
    textarea: textareaConfig(),
    toast: toastConfig(),
    toggleswitch: toggleSwitchConfig(),
  },
})

export const options: PrimeVueConfiguration = {
  ripple: false,
  inputVariant: 'filled',
  theme: {
    preset: oqtepaPreset,
    options: {
      darkModeSelector: '.app-dark',
    },
  },
  pt: {
    button(instance) {
      return instance ? buttonPt(instance) : {}
    },
  },
  /*
   * Тексты самих компонентов PrimeVue живут в plugins/PrimeVue/locale.ts.
   * Объект реактивный и подменяется целиком при смене языка — PrimeVue
   * держит ссылку на него, поэтому календарь переезжает без перезагрузки.
   */
  locale: reactive({ ...PRIME_LOCALES[getLanguage()] }) as PrimeVueLocaleOptions,
}
